import { ResourceQuota } from 'kubernetes-types/core/v1';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { isoDate } from '@/portainer/filters/filters';

import { Link } from '@@/Link';

import { UsedOfHardCell, usedOfHardEntries } from './UsedOfHardCell';

const columnHelper = createColumnHelper<ResourceQuota>();

export function useColumns() {
  return useMemo(
    () => [
      columnHelper.accessor((row) => row.metadata?.name ?? '', {
        header: 'Name',
        id: 'name',
        cell: ({ row: { original: quota } }) => (
          <Link
            to="kubernetes.resourcePools.resourceQuota"
            params={{
              namespace: quota.metadata?.namespace,
              name: quota.metadata?.name,
            }}
            data-cy={`resource-quota-link-${quota.metadata?.name}`}
          >
            {quota.metadata?.name}
          </Link>
        ),
      }),
      columnHelper.accessor((row) => scopeLabel(row), {
        header: 'Scope',
        id: 'scope',
      }),
      columnHelper.accessor((row) => usedOfHardLabel(row), {
        header: 'Used / hard',
        id: 'limits',
        cell: ({ row: { original: quota } }) => (
          <UsedOfHardCell quota={quota} />
        ),
      }),
      columnHelper.accessor((row) => row.metadata?.creationTimestamp ?? '', {
        header: 'Created',
        id: 'created',
        cell: ({ getValue }) => (getValue() ? isoDate(getValue()) : '-'),
      }),
    ],
    []
  );
}

/**
 * A quota with neither scopes nor a scope selector applies to everything in the
 * namespace. Kubernetes restricts what a scoped quota may constrain, so the
 * scope explains why a quota limits only some resources.
 *
 * A selector restricts a quota just as spec.scopes does, so reporting it as
 * unscoped would hide what the quota actually applies to.
 */
function scopeLabel(quota: ResourceQuota) {
  const scopes: Array<string> = quota.spec?.scopes ?? [];
  const selectors = (quota.spec?.scopeSelector?.matchExpressions ?? []).map(
    describeScopeSelector
  );
  const applied = [...scopes, ...selectors];

  return applied.length > 0 ? applied.join(', ') : 'All';
}

function describeScopeSelector(expression: {
  scopeName: string;
  operator: string;
  values?: Array<string>;
}) {
  const values = (expression.values ?? []).join(', ');

  switch (expression.operator) {
    case 'In':
      return `${expression.scopeName} in (${values})`;
    case 'NotIn':
      return `${expression.scopeName} not in (${values})`;
    case 'DoesNotExist':
      return `no ${expression.scopeName}`;
    default:
      return expression.scopeName;
  }
}

/** Sorting and searching read the limits as text, whatever the cell renders. */
function usedOfHardLabel(quota: ResourceQuota) {
  return usedOfHardEntries(quota)
    .map(({ resource, used, hard }) => `${resource} ${used}/${hard}`)
    .join(' ');
}
